import {
  Box,
  Link,
  Flex,
  Grid,
  GridItem,
  Container,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Heading,
  Stack,
} from '@terminal/design-system';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import ChevronDown from '../Icons/ChevronDown';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import slugify from '../../helpers/slugify';

interface FaqItem {
  title: string;
  faq: {
    items: {
      answer: string;
      question: string;
    }[];
  };
}

export default function FaqWithButtons({
  buttons,
  faqs,
}: {
  buttons: {
    icon: JSX.Element;
    text: string;
    titleIcon: JSX.Element;
    url: string;
    active: boolean;
  }[];
  faqs: FaqItem[];
}) {
  const titles = faqs.map((faq) => faq.title);

  const { t } = useTranslation('faq');

  const category = t('menu');

  const [selectValue, setSelectValue] = useState(category);

  const router = useRouter();

  const handleSelect = (e, value) => {
    e.preventDefault();
    setSelectValue(value);
    router.push(`#${slugify(value)}`);
  };

  const FaqButton = ({ button }) => (
    <Link
      href={button.url}
      display="flex"
      flex="1"
      p="0.875rem 1rem"
      mb={{ lg: '2' }}
      alignItems="center"
      color={button.active ? 'grey.900' : 'grey.700'}
      bgColor={button.active ? 'grey.200' : 'white'}
      fontWeight={button.active ? 'bold' : 'normal'}
      borderRadius={{ lg: '0.25rem' }}
      _hover={{ textDecoration: 'none' }}
    >
      <Box mr="2">{button.icon}</Box>
      {button.text}
    </Link>
  );

  const MenuSelect = ({ values }) => (
    <Menu flip={false} gutter={4} matchWidth>
      {({ isOpen }) => (
        <>
          <MenuButton
            as={Button}
            rightIcon={
              <ChevronDown
                transform={{
                  base: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                }}
                transition="transform .3s ease-in-out"
                color="green.500"
              />
            }
            minH="12"
            color={selectValue === category && 'grey.600'}
            bgColor="white"
            borderRadius="0.25rem"
            borderWidth="0.0625rem"
            boxShadow="none"
            borderColor={'grey.300'}
            fontWeight="normal"
            w="100%"
            textAlign="left"
            _hover={{ bg: 'white' }}
            _active={{ bg: 'white' }}
            _expanded={{ bg: 'white', borderColor: 'green.500' }}
          >
            {selectValue}
          </MenuButton>
          <MenuList boxShadow="none" borderRadius="0.25rem" minWidth="100%" py="1rem" px="1rem">
            {values.map((value) => {
              return (
                <MenuItem
                  key={value}
                  w="100%"
                  p="0.625rem"
                  onClick={(e) => handleSelect(e, value)}
                  _hover={{
                    bgColor: 'grey.200',
                    textDecoration: 'none',
                    boxShadow: 'none',
                  }}
                >
                  {value}
                </MenuItem>
              );
            })}
          </MenuList>
        </>
      )}
    </Menu>
  );

  const FaqBlock = ({ item }: { item: FaqItem }) => {
    const [accIndex, setAccIndex] = useState<number[]>([]);

    const handleOnAccordionClick = (e) => {
      setAccIndex([...e]);
    };

    return (
      <Box
        p="8"
        borderColor="grey.300"
        borderRadius="0.5rem"
        borderWidth="0.0625rem"
        id={slugify(item.title)}
        scrollMarginTop={{ base: '4.5rem', lg: '7.5rem' }}
      >
        <Box display={{ lg: 'flex' }} alignItems="center" justifyContent="space-between" mb="1">
          <Heading
            as="h2"
            fontSize={{ base: '1.5rem' }}
            lineHeight={{ base: '1.75rem' }}
            fontWeight="semibold"
          >
            {item.title}
          </Heading>
          <Flex alignItems="center" mt={{ base: '2', lg: '0' }}>
            <Button
              variant="link"
              fontWeight="normal"
              color={accIndex.length < item.faq.items.length ? 'green.500' : 'grey.400'}
              _hover={{ textDecoration: 'none' }}
              onClick={() => setAccIndex(Array.from(Array(item.faq.items.length).keys()))}
              _focus={{
                boxShadow: 'none',
              }}
            >
              {t('expand')}
            </Button>
            <Box w="0.0625rem" height="1.25rem" bgColor="grey.700" mx="2" />
            <Button
              variant="link"
              fontWeight="normal"
              color={accIndex.length > 0 ? 'green.500' : 'grey.400'}
              _hover={{ textDecoration: 'none' }}
              onClick={() => setAccIndex([])}
              _focus={{
                boxShadow: 'none',
              }}
            >
              {t('collapse')}
            </Button>
          </Flex>
        </Box>
        {/* TODO: remove this ts-ignore */}
        {/* @ts-ignore */}
        <Accordion allowMultiple index={accIndex} onChange={handleOnAccordionClick}>
          {item.faq.items.map((itemFaq, index) => (
            <AccordionItem
              key={index}
              borderTop={index === 0 && 'none'}
              id={`${index}`}
              borderBottom="1px solid"
              borderBottomColor="ui.secondary"
              sx={{
                ':last-of-type': {
                  borderBottom: 'none',
                },
              }}
            >
              <Heading as="h3">
                <AccordionButton
                  px="0"
                  py={{ base: '4' }}
                  _expanded={{ bg: 'transparent' }}
                  _hover={{
                    bgColor: 'transparent',
                  }}
                  _focus={{
                    boxShadow: 'none',
                  }}
                >
                  <Box
                    flex="1"
                    textAlign="left"
                    fontWeight="semibold"
                    fontSize={{ base: 'lg', lg: 'xl' }}
                    pr={{ base: '2', lg: '4' }}
                  >
                    {itemFaq.question}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </Heading>
              <AccordionPanel
                pl="0"
                fontSize={{ base: 'md' }}
                dangerouslySetInnerHTML={{ __html: itemFaq.answer }}
                color="grey.700"
                pb={4}
                sx={{
                  'p:not(:last-of-type)': {
                    mb: 5,
                  },
                  a: {
                    textDecoration: 'underline',
                  },
                }}
              />
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
    );
  };

  return (
    <Container mt={{ base: '8', lg: '12' }}>
      <Grid
        gridTemplateColumns={{ lg: '2.5fr 7.5fr', xl: '2.1fr 7.9fr' }}
        gridGap={{ base: '9', lg: '1.125rem' }}
      >
        <GridItem>
          <Box position={{ lg: 'sticky' }} top={{ lg: '40' }} minH={{ lg: '21.875rem' }}>
            {buttons?.length > 0 && (
              <Box
                display={{ base: 'flex', lg: 'block' }}
                border={{ base: '0.0625rem solid #D5DADC', lg: 'none' }}
                borderRadius={{ base: '0.25rem', lg: '0' }}
                overflow={{ base: 'hidden', lg: 'visible' }}
                mb={{ base: '4', lg: '2' }}
              >
                {buttons.map((button) => (
                  <FaqButton button={button} key={button.text} />
                ))}
              </Box>
            )}
            <MenuSelect values={titles} />
          </Box>
        </GridItem>
        <GridItem>
          <Flex alignItems="center" mb={{ base: '1.25rem', lg: '3rem' }}>
            <Heading
              as="h1"
              fontSize={{ base: '2rem', lg: '2.625rem' }}
              lineHeight={{ base: '2.5rem', lg: '3rem' }}
              mr={{ base: '4', lg: '7' }}
            >
              {buttons.map((button) => button.active && button.text)}
            </Heading>
            {buttons.map((button) => button.active && button.titleIcon)}
          </Flex>
          <Stack spacing="5">
            {faqs?.length > 0 ? (
              faqs.map((faq) => <FaqBlock key={faq.title} item={faq} />)
            ) : (
              <Box>No FAQs in this category or FAQs are not translated yet</Box>
            )}
          </Stack>
        </GridItem>
      </Grid>
    </Container>
  );
}
